import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Driver {
  id: string;
  user_id: string;
  company_id: string;
  license_number: string;
  license_expiry: string;
  status: 'available' | 'on_mission' | 'off_duty';
  current_location_lat: number | null;
  current_location_lng: number | null;
  license_verified_at?: string | null;
  license_categories?: string[] | null;
  created_at: string;
  updated_at: string;
  users?: any;
}

export const useDrivers = () => {
  const { data: drivers = [], isLoading, error } = useQuery({
    queryKey: ['drivers'],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Non authentifié');

        // Récupérer le company_id de l'utilisateur
        const { data: userData } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', user.id)
          .single();

        if (!userData?.company_id) throw new Error('Company not found');

        const { data, error } = await supabase
          .from('drivers')
          .select(`
            *,
            users (
              first_name,
              last_name,
              email,
              phone
            )
          `)
          .eq('company_id', userData.company_id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        return data as Driver[];
      } catch (err) {
        toast.error('Erreur lors du chargement des chauffeurs');
        throw err;
      }
    },
  });

  const availableDrivers = drivers.filter(d => d.status === 'available');

  return {
    drivers,
    availableDrivers,
    isLoading,
    error,
  };
};

export const useAvailableDriversForMission = (pickupDate?: string, deliveryDate?: string) => {
  const queryClient = useQueryClient();
  
  const { data: availableDrivers = [], isLoading } = useQuery({
    queryKey: ['available-drivers-for-mission', pickupDate, deliveryDate],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Non authentifié');

        // Get all drivers with their assigned jobs and orders
        const { data: drivers, error: driversError } = await supabase
          .from('drivers')
          .select(`
            *,
            users (
              first_name,
              last_name,
              email,
              phone
            )
          `)
          .order('created_at', { ascending: false });

        if (driversError) throw driversError;

        if (!pickupDate || !deliveryDate) {
          return drivers?.filter(d => d.status === 'available') || [];
        }

        // Get all active transport jobs
        const { data: jobs, error: jobsError } = await supabase
          .from('transport_jobs')
          .select(`
            driver_id,
            orders (
              pickup_date,
              delivery_date,
              status
            )
          `)
          .not('driver_id', 'is', null)
          .is('completed_at', null)
          .is('declined_at', null);

        if (jobsError) throw jobsError;

        // Filter drivers available for the mission
        const newPickup = new Date(pickupDate);
        const newDelivery = new Date(deliveryDate);

        return drivers?.filter(driver => {
          // Block drivers with missing or expired license (verified by OCR)
          const d: any = driver;
          if (!d.license_verified_at) return false;
          if (!d.license_expiry) return false;
          if (new Date(d.license_expiry) <= newDelivery) return false;

          const driverJobs = jobs?.filter(job => job.driver_id === driver.id) || [];
          if (driverJobs.length === 0) return true;

          const hasConflict = driverJobs.some(job => {
            if (!job.orders) return false;
            const jobPickup = new Date(job.orders.pickup_date);
            const jobDelivery = new Date(job.orders.delivery_date);
            return !(newDelivery <= jobPickup || newPickup >= jobDelivery);
          });

          return !hasConflict;
        }) || [];
      } catch (err) {
        toast.error('Erreur lors du chargement des chauffeurs disponibles');
        throw err;
      }
    },
    enabled: !!pickupDate && !!deliveryDate,
  });

  return {
    availableDrivers,
    isLoading,
  };
};

export const useUpdateDriver = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: updateDriver, isPending: isUpdating } = useMutation({
    mutationFn: async ({ id, first_name, last_name, ...driverData }: {
      id: string;
      first_name?: string;
      last_name?: string;
      license_number?: string;
      license_expiry?: string;
      status?: 'available' | 'on_mission' | 'off_duty';
      current_location_lat?: number | null;
      current_location_lng?: number | null;
    }) => {
      try {
        // Get driver first to get user_id
        const { data: driver, error: fetchError } = await supabase
          .from('drivers')
          .select('user_id')
          .eq('id', id)
          .single();

        if (fetchError) throw fetchError;

        // Update users table if first_name or last_name provided
        if (first_name !== undefined || last_name !== undefined) {
          const userUpdates: any = {};
          if (first_name !== undefined) userUpdates.first_name = first_name;
          if (last_name !== undefined) userUpdates.last_name = last_name;

          const { error: userError } = await supabase
            .from('users')
            .update(userUpdates)
            .eq('id', driver.user_id);

          if (userError) throw userError;
        }

        // Update drivers table
        const { data, error } = await supabase
          .from('drivers')
          .update(driverData)
          .eq('id', id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } catch (err) {
        toast.error('Erreur lors de la mise à jour du chauffeur');
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      toast.success('Chauffeur mis à jour avec succès');
    },
  });

  return { updateDriver, isUpdating };
};

export const useDeleteDriver = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: deleteDriver, isPending: isDeleting } = useMutation({
    mutationFn: async (driverId: string) => {
      try {
        const { error } = await supabase
          .from('drivers')
          .delete()
          .eq('id', driverId);

        if (error) throw error;
      } catch (err) {
        toast.error('Erreur lors de la suppression du chauffeur');
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      toast.success('Chauffeur supprimé avec succès');
    },
  });

  return { deleteDriver, isDeleting };
};
